import React from 'react';
import {
  Sparkles, BookOpen, Clock, Target, CheckCircle2, Lightbulb, Layers, RefreshCw, Printer, Save, FileText, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function GenerateLessonTab({
  lessonForm, setLessonForm, loading, generateLesson,
  generatedLesson, saveLesson, printLesson, shareToAcademic, sharingToAcademic,
  getSubjectOptions, getYearOptions, shareLabel = 'Share to Academic'
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Input Form */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Lesson Details
          </CardTitle>
          <CardDescription>Configure your lesson parameters</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="topic">Topic *</Label>
            <Input
              id="topic"
              placeholder="e.g., Food Groups and Nutrition"
              value={lessonForm.topic}
              onChange={(e) => setLessonForm({...lessonForm, topic: e.target.value})}
              data-testid="input-topic"
            />
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Select
              value={lessonForm.subject}
              onValueChange={(value) => setLessonForm({...lessonForm, subject: value})}
            >
              <SelectTrigger data-testid="select-subject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getSubjectOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Year Level</Label>
              <Select
                value={String(lessonForm.year_level)}
                onValueChange={(value) => setLessonForm({...lessonForm, year_level: parseInt(value)})}
              >
                <SelectTrigger data-testid="select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Duration</Label>
              <Select
                value={String(lessonForm.duration)}
                onValueChange={(value) => setLessonForm({...lessonForm, duration: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                    <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Student Abilities</Label>
            <Select
              value={lessonForm.student_abilities}
              onValueChange={(value) => setLessonForm({...lessonForm, student_abilities: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mixed">Mixed Abilities</SelectItem>
                <SelectItem value="advanced">Advanced Learners</SelectItem>
                <SelectItem value="struggling">Struggling Learners</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Teaching Style</Label>
            <Select
              value={lessonForm.teaching_style}
              onValueChange={(value) => setLessonForm({...lessonForm, teaching_style: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="interactive">Interactive & Discussion</SelectItem>
                <SelectItem value="traditional">Traditional Direct</SelectItem>
                <SelectItem value="hands-on">Hands-on Experiential</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            className="w-full mt-4" 
            onClick={generateLesson}
            disabled={loading}
            data-testid="btn-generate-lesson"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Lesson Plan
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Generated Lesson Output */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Generated Lesson Plan
            </span>
            {generatedLesson && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => printLesson(generatedLesson)}
                  data-testid="btn-print-lesson"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => saveLesson(generatedLesson)}
                  data-testid="btn-save-lesson"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
                {shareToAcademic && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => shareToAcademic(generatedLesson)}
                    disabled={sharingToAcademic}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-share-lesson-to-academic"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {sharingToAcademic ? 'Sharing...' : shareLabel}
                  </Button>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!generatedLesson ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Enter a topic and click Generate to create your lesson plan</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold">{generatedLesson.title}</h3>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    {generatedLesson.duration || `${lessonForm.duration} min`}
                  </Badge>
                  <Badge variant="outline">Year {lessonForm.year_level}</Badge>
                </div>
              </div>

              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  Learning Objectives
                </h4>
                <ul className="space-y-1">
                  {generatedLesson.objectives?.map((obj, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Lightbulb className="h-4 w-4 text-yellow-600" />
                  Introduction
                </h4>
                <p className="text-sm text-muted-foreground bg-yellow-50 p-3 rounded-lg">
                  {generatedLesson.introduction}
                </p>
              </div>

              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Layers className="h-4 w-4 text-purple-600" />
                  Activities
                </h4>
                <div className="space-y-3">
                  {generatedLesson.activities?.map((activity, idx) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium">{activity.name}</h5>
                        <Badge variant="secondary">{activity.duration}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Materials</h4>
                  <p className="text-sm text-blue-700">{generatedLesson.materials}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Assessment</h4>
                  <p className="text-sm text-green-700">{generatedLesson.assessment}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-800 mb-2">Homework</h4>
                  <p className="text-sm text-orange-700">{generatedLesson.homework}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
